/**
 * Thesis card domain, frontend mirror of
 * backend/src/services/chat_agent/thesis_card.py.
 *
 * The card is not stored client-side (transcript-as-state, spec §1): it is a
 * projection of chat messages' `metadata`. Tolerant coercion (clamp + drop,
 * never throw) and the replay rule are kept semantics-identical to the backend
 * so both sides project the same card from the same transcript.
 */
import type { ChatMessage } from './chatApi';
import type { ThesisCard } from './chatStream';

const DURATIONS = ['Core', 'Growth', 'Speculative'];
// Incident #5 backstop (mirrors _DURATION_ALIASES in the backend's
// thesis_card.py — the two coercions must stay semantics-identical): flash may
// echo the zh DISPLAY string back into the enum field (zh options render
// 核心/成長/投機). A silent drop would leave the card forever incomplete, so the
// tolerance layer maps traditional + simplified forms; English case variants
// keep the capitalize path in coerceCard.
const DURATION_ALIASES: Record<string, ThesisCard['duration']> = {
  核心: 'Core',
  成長: 'Growth',
  增长: 'Growth',
  投機: 'Speculative',
  投机: 'Speculative',
};
const LIST_MAX = 10;
const ITEM_MAX = 200;
const STATEMENT_MAX = 1000;
const PROGRESS_FIELDS = 4; // duration, statement, pillars, risks (conviction left the flow)

function isDict(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function cleanStrList(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  const out: string[] = [];
  const seen = new Set<string>();
  for (const item of raw) {
    if (typeof item !== 'string') continue;
    const s = item.trim().slice(0, ITEM_MAX);
    if (!s || seen.has(s.toLowerCase())) continue;
    seen.add(s.toLowerCase());
    out.push(s);
    if (out.length >= LIST_MAX) break;
  }
  return out;
}

function clampInt(raw: unknown, lo: number, hi: number): number | null {
  if (typeof raw !== 'number' || Number.isNaN(raw)) return null;
  if (!Number.isFinite(raw)) return null;
  return Math.max(lo, Math.min(hi, Math.trunc(raw)));
}

function cleanPrice(raw: unknown): number | null {
  if (typeof raw !== 'number' || !Number.isFinite(raw) || raw < 0) return null;
  return raw;
}

function cleanDate(raw: unknown): string | null {
  if (typeof raw !== 'string') return null;
  const s = raw.slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return null; // fromisoformat is stricter than Date
  return Number.isNaN(new Date(`${s}T00:00:00Z`).getTime()) ? null : s;
}

/** Tolerant coercion of an agent/agent+user card payload (spec §6 relaxed).
 * Returns null when there is no usable card — symbol is the one field
 * backend coercion guarantees, so it gates the whole card. */
export function coerceCard(raw: unknown): ThesisCard | null {
  if (!isDict(raw)) return null;
  const symbol = raw.symbol;
  if (typeof symbol !== 'string' || !symbol.trim()) return null;
  const duration = raw.duration;
  const capitalized =
    typeof duration === 'string'
      ? duration.charAt(0).toUpperCase() + duration.slice(1).toLowerCase()
      : null;
  const durationValue: ThesisCard['duration'] =
    typeof duration === 'string' && duration in DURATION_ALIASES
      ? DURATION_ALIASES[duration]
      : capitalized && DURATIONS.includes(capitalized)
        ? (capitalized as ThesisCard['duration'])
        : null;
  const statement = raw.statement;
  return {
    symbol: symbol.trim().toUpperCase().slice(0, 16),
    duration: durationValue,
    conviction_level: clampInt(raw.conviction_level, 1, 5),
    statement:
      typeof statement === 'string' && statement.trim()
        ? statement.trim().slice(0, STATEMENT_MAX)
        : null,
    pillars: cleanStrList(raw.pillars),
    risks: cleanStrList(raw.risks),
    entry_price: cleanPrice(raw.entry_price),
    stop_loss_price: cleanPrice(raw.stop_loss_price),
    take_profit_price: cleanPrice(raw.take_profit_price),
    remind_at: cleanDate(raw.remind_at),
  };
}

/** Patch a card with user sheet edits: coerce the MERGED dict so every edit
 * passes the same tolerance rules as a full snapshot. */
function applyEdit(card: ThesisCard, fields: unknown): ThesisCard {
  if (!isDict(fields) || Object.keys(fields).length === 0) return card;
  const merged: Record<string, unknown> = { ...card, ...fields };
  merged.symbol = card.symbol; // never editable
  return coerceCard(merged) ?? card;
}

/** Full session replay (spec §4.1): the projected card plus whether the newest
 * lifecycle event is a save and/or a dismissal. `saved` is true iff a
 * `thesis_saved` receipt is newer than the winning snapshot, `dismissed` iff a
 * `thesis_dismissed` receipt is — the card is STILL returned for viewing
 * (the sheet shows the last state), but the draft bar must not re-arm for a
 * session the user dismissed or already saved. An edit alone does not resume
 * the draft; a NEW snapshot after either receipt does. */
export function replaySession(messages: ChatMessage[]): {
  card: ThesisCard | null;
  saved: boolean;
  dismissed: boolean;
} {
  const edits: unknown[] = [];
  let saved = false;
  let dismissed = false;
  for (let i = messages.length - 1; i >= 0; i--) {
    const meta = messages[i]?.metadata;
    if (!isDict(meta)) continue;
    const edit = meta.thesis_edit; // checked first: a meta with both is an edit
    if (isDict(edit)) {
      edits.push(edit);
      continue;
    }
    if (meta.thesis_saved != null) {
      saved = true; // receipt is newer than any snapshot seen so far
      continue;
    }
    if (meta.thesis_dismissed != null) {
      dismissed = true; // same ordering rule as the save receipt
      continue;
    }
    const card = coerceCard(meta.thesis_card);
    if (card !== null) {
      let folded = card;
      for (let j = edits.length - 1; j >= 0; j--) {
        folded = applyEdit(folded, edits[j]); // oldest→newest
      }
      return { card: folded, saved, dismissed };
    }
  }
  return { card: null, saved: false, dismissed: false };
}

/** Latest thesis_card snapshot wins; thesis_edit patches newer than it fold
 * in, oldest→newest (spec §4.1 replay rule). `messages` arrive in
 * ChatConversation order (oldest first), so the scan runs backwards. */
export function replayCard(messages: ChatMessage[]): ThesisCard | null {
  return replaySession(messages).card;
}

/** One unsaved thesis draft of the multi-slot projection (T16). `pos` is the
 * winning snapshot's index in `messages` — the recency key the consumer
 * orders entries (and merges cross-kind entries) by. */
export interface ThesisDraftEntry {
  symbol: string;
  card: ThesisCard;
  pos: number;
}

/** Frontend twin of the T16 receipt-symbol normalization (backend
 * session_artifacts._thesis_receipt_target, itself coerce_card's rule). */
function receiptSymbol(receipt: unknown): string | null {
  if (!isDict(receipt)) return null;
  const raw = receipt.symbol;
  if (typeof raw !== 'string' || !raw.trim()) return null;
  return raw.trim().toUpperCase().slice(0, 16);
}

/** Multi-slot replay (chat-centric revamp T16): EVERY unsaved thesis draft of
 * the session, keyed by symbol — the single-slot {@link replaySession}
 * generalizes to one draft per symbol so a displaced draft can come back via
 * a return chip. Same lifecycle rules, lifted per symbol:
 *  - the LATEST snapshot per symbol wins, edits fold into the then-latest
 *    draft oldest→newest (an edit belongs to whichever card the sheet showed);
 *  - a `thesis_saved` receipt that names its symbol closes ONLY that symbol
 *    (the T16 receipt payload); a symbol-less legacy receipt stays kind-wide
 *    (closes every draft) — backward compatible with old transcripts;
 *  - a `thesis_dismissed` receipt (symbol-less by shape) closes the
 *    then-LATEST draft — the one whose bar the ✕ sat on;
 *  - a NEW snapshot after any receipt re-arms just its symbol.
 * Returns entries newest-snapshot-first. */
export function replayThesisEntries(messages: ChatMessage[]): ThesisDraftEntry[] {
  const drafts = new Map<string, { card: ThesisCard; pos: number }>();
  const order: string[] = []; // symbols, oldest-snapshot-first (tail = then-latest)
  const removeDraft = (symbol: string) => {
    drafts.delete(symbol);
    const at = order.indexOf(symbol);
    if (at !== -1) order.splice(at, 1);
  };
  for (let i = 0; i < messages.length; i++) {
    const meta = messages[i]?.metadata;
    if (!isDict(meta)) continue;
    const edit = meta.thesis_edit; // checked first: a meta with both is an edit
    if (isDict(edit)) {
      const latest = order[order.length - 1];
      const current = latest !== undefined ? drafts.get(latest) : undefined;
      if (current) drafts.set(latest, { card: applyEdit(current.card, edit), pos: current.pos });
      continue;
    }
    if (meta.thesis_saved != null) {
      const symbol = receiptSymbol(meta.thesis_saved);
      if (symbol !== null) removeDraft(symbol);
      else {
        drafts.clear(); // legacy receipt: kind-wide
        order.length = 0;
      }
      continue;
    }
    if (meta.thesis_dismissed != null) {
      const latest = order[order.length - 1];
      if (latest !== undefined) removeDraft(latest);
      continue;
    }
    const card = coerceCard(meta.thesis_card);
    if (card !== null) {
      // Re-snapshot moves the symbol to the recency tail: `order` tracks the
      // LATEST snapshot per symbol (newest-snapshot-first on reversal), not
      // first-snapshot order.
      const at = order.indexOf(card.symbol);
      if (at !== -1) order.splice(at, 1);
      order.push(card.symbol);
      drafts.set(card.symbol, { card, pos: i });
    }
  }
  return order
    .slice()
    .reverse()
    .map((symbol) => {
      const draft = drafts.get(symbol)!;
      return { symbol, card: draft.card, pos: draft.pos };
    });
}

export function cardProgress(card: ThesisCard): { filled: number; total: number } {
  const filled =
    (card.duration != null ? 1 : 0) +
    (card.statement ? 1 : 0) +
    (card.pillars.length > 0 ? 1 : 0) +
    (card.risks.length > 0 ? 1 : 0);
  return { filled, total: PROGRESS_FIELDS };
}

export function saveEnabled(card: ThesisCard): boolean {
  // Conviction no longer gates the save (removed from the builder flow); the
  // field still rides along on persisted cards for compatibility.
  return Boolean(card.duration && card.statement);
}
